<script>
  import notify from "../../../../Script/notify"

  export let field

  /**
   * @typedef {Object} closeNotification
   * @property {Function} close - Closes the notification.
   */
  /**
   * @type {closeNotification}
   */
  let closeNotification = {
    close() {},
  }

  const ALLOWED_CHARS_REGEX = /^[A-Za-z\s()+₹.@$%&*-][A-Za-z\d\s()+₹.@$%&*-]*$/
  /**
   * @type {HTMLInputElement}
   */
  let keyInput

  /**
   * @description check max character function for metadata value
   */
  const checkMaxCharacters = async (key) => {
    field[key] = field[key].trim()

    const MAX_CHARACTERS = key == "key" ? 30 : 80

    if (field[key].length > MAX_CHARACTERS) {
      closeNotification.close()

      field[key] = field[key].slice(0, MAX_CHARACTERS)

      closeNotification = notify.info(`Cannot enter more than ${MAX_CHARACTERS} characters`)
    }
  }

  /**
   * Validates the input field to allow only specific characters.
   * @param {Event} e - The event object.
   */
  const checkSpecialChars = (e) => {
    try {
      // Close any existing notifications
      closeNotification.close()

      // Destructure the type and key properties from the event object
      let { type, key } = e

      if (type === "keypress" && !ALLOWED_CHARS_REGEX.test(field.key + key)) {
        try {
          closeNotification.close()

          e.preventDefault()

          field.key += key

          const info = `PAGE: Edit Document Metadata (Single Publish)\nEVENT: Validating invalid characters for the metadata value on input keypress`
          closeNotification = notify.danger(`Allowed characters: <strong>Letters</strong>, <strong>Numbers</strong>, and <strong>()+₹.@$%&*-</strong> (Numbers at the leading place are not allowed)`, info)

          setTimeout(() => {
            field.key = field.key.slice(0, -1)
          }, 100)
        } catch (error) {
          throw error
        }
      } else if (type === "paste") {
        // Handle the pasted text
        closeNotification.close()
        e.preventDefault()

        let clipboardData = e.clipboardData || window.clipboardData
        // Retrieve the pasted text from the clipboard
        let pastedText = clipboardData.getData("text/plain")
        // Remove any characters that don't match the allowed characters regex
        let cleanedText = pastedText.replace(/[^A-Za-z\d-\s()+₹.@$%&*-]/g, "")
        if (!field.key) {
          // Remove leading numbers followed by a non-alphabetic character
          cleanedText = cleanedText.replace(/^\d+([^A-Za-z]|$)/, "")
        }

        if (!ALLOWED_CHARS_REGEX.test(pastedText)) {
          cleanedText = cleanedText.trim()

          if (cleanedText !== pastedText) {
            // Display a notification about removed characters and restrictions
            const info = `PAGE: Edit Document Metadata (Single Publish)\nEVENT: Validating invalid characters for the metadata value on paste`
            closeNotification = notify.danger("Only &emsp;<br/> <strong>Letters</strong> &emsp;<br/> <strong>Numbers</strong> and &emsp;<br/> <strong>()+₹.@$%&*-</strong> are allowed. Removing Numbers at Leading place and non-specified Characters", info)
          }
        }

        // Get the selected text range
        const selectionStart = keyInput.selectionStart
        const selectionEnd = keyInput.selectionEnd

        // Replace the selected text with the cleaned text
        field.key = field.key.substring(0, selectionStart) + cleanedText + field.key.substring(selectionEnd)

        // Move the cursor to the end of the inserted text
        const newCursorPosition = selectionStart + cleanedText.length
        keyInput.setSelectionRange(newCursorPosition, newCursorPosition)

        // Perform additional actions related to input changes
        inputChange()

        // Check for maximum character limit
        checkMaxCharacters("key")
      }
    } catch (error) {
      console.error(error)
      const info = `PAGE: Edit Document Metadata (Single Publish)\nEVENT: Validating invalid characters for the metadata value on input keypress/ paste`
      notify.danger(error, info)
    }
  }

  /**
   * @description - Function to change input key and value in accordian
   */
  const inputChange = async () => {
    // Close the digitRevertNotification if it is open.
    closeNotification.close()

    // Clean the text by removing characters that are not allowed.
    let cleanedText = field.key.replace(/[^A-Za-z\d-\s()+₹.@$%&*-]/g, "")

    const info = `PAGE: Edit Document Metadata (Single Publish)\nEVENT: Validating metadata value on input change`

    // If the cleaned text does not match the allowedCharsRegex, remove leading digits.
    if (!ALLOWED_CHARS_REGEX.test(cleanedText)) {
      cleanedText = cleanedText.replace(/^\d+([^A-Za-z]|$)/, "")
      cleanedText = cleanedText.replace(/^\d/, "")

      // If the input value still does not match the allowedCharsRegex, trim the text.
      if (!ALLOWED_CHARS_REGEX.test(field.key)) {
        cleanedText = cleanedText.trim()

        // If the cleaned text is different from the input value, show a notification.
        if (cleanedText !== field.key) {
          closeNotification = notify.danger("Numbers not allowed at Leading place", info)
        }
      }
    }

    // Update the input value with the cleaned text.
    field.key = cleanedText

    // Check if the input value contains only digits.
    if (/^\d+$/.test(field.key)) {
      // Close the digitNotification if any
      closeNotification.close()

      // Clear the input value.
      field.key = ""

      // Show a notification indicating that only numbers are not allowed.
      closeNotification = notify.danger("Only Numbers not allowed", info)
    }
  }
</script>

<div class="mt-3 flex w-full flex-col items-center justify-end gap-1 pl-3 mobile:flex-row mobile:gap-6">
  <div class="w-full mobile:w-2/6">
    <input
      on:blur={() => {
        checkMaxCharacters("key")
      }}
      on:blur
      bind:this={keyInput}
      on:paste={checkSpecialChars}
      on:keypress={checkSpecialChars}
      id="key{field.id}"
      required
      type="text"
      bind:value={field.key}
      placeholder="Enter key"
      class="w-full rounded-md border border-[#a0a1a580] bg-transparent px-3 py-2 pl-5 text-sm text-gray-800 outline-none placeholder:font-normal placeholder:text-gray-200 invalid:animate-shake invalid:border-red-500 invalid:placeholder-red-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:invalid:border-red-500 focus:invalid:ring-red-500 dark:text-white sm:text-sm"
    />
  </div>
  <div class="flex w-full items-center gap-3 mobile:w-4/6">
    <input
      on:blur={() => {
        checkMaxCharacters("value")
      }}
      on:paste={() => {
        checkMaxCharacters("value")
      }}
      disabled={!field.key.trim()}
      required
      bind:value={field.value}
      placeholder="Enter value"
      type="text"
      class="w-full rounded-md border border-[#a0a1a580] bg-transparent px-3 py-2 pl-5 text-sm text-gray-800 outline-none placeholder:font-normal placeholder:text-gray-200 invalid:animate-shake invalid:border-red-500 invalid:placeholder-red-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:invalid:border-red-500 focus:invalid:ring-red-500 dark:text-white sm:text-sm"
    />
    <button on:click type="button" data-tooltip="Delete field" tooltip-position="top" class="flex aspect-square w-8 cursor-pointer items-center justify-center rounded-full text-gray-400 hover:bg-red-500 hover:text-white focus-visible:bg-red-500 focus-visible:text-white focus-visible:outline-none">
      <span>
        <i class="fa-solid fa-trash-can" />
      </span>
    </button>
  </div>
</div>
